// Read-only structural preview of a page's blocks (BIZ-PB-03).
//
// Distinct from the Puck edit canvas: this renders the persisted block array
// exactly as it will be stored, so an editor can sanity-check structure/order
// before saving. Form/slider embeds resolve their referenced entity's title
// from the cached list queries; a dangling reference falls back to the raw id.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Empty } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { formsApi } from '@api/forms';
import { slidersApi } from '@api/sliders';
import { componentsApi } from '@api/components';
import type { ComponentPaletteItem } from '@features/component-studio/types';
import { toRuntimeComponentDef } from '../builder/runtimeComponents';
import { RuntimeComponentRender } from '../builder/RuntimeComponentRender';
import {
  isRuntimeBlockKey,
  type CardProps,
  type CtaProps,
  type HeroProps,
  type MapEmbedProps,
  type PageBlock,
  type RichTextProps,
} from './blockTypes';
import {
  CardGridView,
  CtaView,
  EmbedPlaceholder,
  HeroView,
  MapEmbedView,
  RichTextView,
} from './renderers';

interface BlockPreviewProps {
  blocks: PageBlock[];
}

export function BlockPreview({ blocks }: BlockPreviewProps) {
  const { t } = useTranslation('pages');
  const { data: forms = [] } = useQuery({
    queryKey: ['forms', 'list', 'all'],
    queryFn: () => formsApi.list(),
    staleTime: 60_000,
  });
  const { data: sliders = [] } = useQuery({
    queryKey: ['sliders', 'list', 'all'],
    queryFn: () => slidersApi.list(),
    staleTime: 60_000,
  });
  // Runtime component blokları (cmp_*) için published palet — arşivli/silinmiş
  // bir referans palette bulunmaz → yapısal placeholder'a düşer.
  const { data: palette = [] } = useQuery({
    queryKey: ['component-palette'],
    queryFn: () => componentsApi.palette(),
    staleTime: 60_000,
  });

  const formNames = useMemo(
    () => new Map(forms.map((f) => [f.id, f.title])),
    [forms],
  );
  const sliderNames = useMemo(
    () => new Map(sliders.map((s) => [s.id, s.name])),
    [sliders],
  );
  const runtimeByKey = useMemo(
    () => new Map(palette.map((p) => [p.blockKey, p])),
    [palette],
  );

  if (blocks.length === 0) {
    return <Empty description={t('builder.preview.empty')} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {blocks.map((block, i) => (
        <div key={i}>{renderBlock(block, { t, formNames, sliderNames, runtimeByKey })}</div>
      ))}
    </div>
  );
}

interface RenderCtx {
  t: ReturnType<typeof useTranslation<'pages'>>['t'];
  formNames: Map<string, string>;
  sliderNames: Map<string, string>;
  runtimeByKey: Map<string, ComponentPaletteItem>;
}

function renderBlock(block: PageBlock, ctx: RenderCtx) {
  const p = block.props;
  // Runtime component bloğu: componentJson varsa atomik ağaç render, yoksa legacy
  // interpolate — karar Puck edit-canvas'ıyla ortak `RuntimeComponentRender`'da (K-26).
  if (isRuntimeBlockKey(block.blockType)) {
    const item = ctx.runtimeByKey.get(block.blockType);
    if (!item) {
      return (
        <EmbedPlaceholder kind={ctx.t('builder.blocks.runtime.label')} label={block.blockType} />
      );
    }
    return <RuntimeComponentRender def={toRuntimeComponentDef(item)} values={p} />;
  }
  switch (block.blockType) {
    case 'hero':
      return <HeroView {...(p as unknown as HeroProps)} />;
    case 'richText':
      return <RichTextView {...(p as unknown as RichTextProps)} />;
    case 'cardGrid':
      return <CardGridView cards={(p.cards as CardProps[]) ?? []} />;
    case 'cta':
      return <CtaView {...(p as unknown as CtaProps)} />;
    case 'formEmbed': {
      const id = typeof p.formId === 'string' ? p.formId : '';
      return (
        <EmbedPlaceholder
          kind={ctx.t('builder.blocks.formEmbed.label')}
          label={ctx.formNames.get(id) ?? id ?? '—'}
        />
      );
    }
    case 'sliderEmbed': {
      const id = typeof p.sliderId === 'string' ? p.sliderId : '';
      return (
        <EmbedPlaceholder
          kind={ctx.t('builder.blocks.sliderEmbed.label')}
          label={ctx.sliderNames.get(id) ?? id ?? '—'}
        />
      );
    }
    case 'mapEmbed':
      return <MapEmbedView {...(p as unknown as MapEmbedProps)} />;
    default:
      return null;
  }
}
